import type { RuntimeConfig } from "@crialume/sdk";
import { parseOptions, z } from "../validation";

const RuntimeConfigSchema = z.looseObject({
  appName: z.string().check(z.minLength(1)),
  apiBaseUrl: z.string().check(z.minLength(1)),
  features: z.record(z.string(), z.boolean()),
  disabledModules: z.array(z.string()),
});

export interface LoadRuntimeConfigOptions<T extends RuntimeConfig> {
  /** URL do JSON servido junto com o app (ex.: `${BASE_URL}config.json`). */
  url: string;
  /** Valores usados quando o arquivo não existe ou omite chaves. */
  defaults: T;
  fetchImpl?: typeof fetch;
}

/**
 * Carrega a configuração de runtime ANTES de montar o React. Permite promover
 * o mesmo build entre ambientes trocando apenas o `config.json` no servidor.
 */
export async function loadRuntimeConfig<T extends RuntimeConfig>(
  options: LoadRuntimeConfigOptions<T>,
): Promise<T> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  let remote: Record<string, unknown> = {};

  try {
    const response = await fetchImpl(options.url, { cache: "no-store" });
    if (response.ok) remote = (await response.json()) as Record<string, unknown>;
  } catch {
    // Sem config.json: segue com os defaults.
  }

  const merged = {
    ...options.defaults,
    ...remote,
    features: {
      ...options.defaults.features,
      ...(remote["features"] as Record<string, boolean> | undefined),
    },
  };

  const parsed = RuntimeConfigSchema.safeParse(merged, parseOptions);
  if (!parsed.success) {
    throw new Error(`config.json inválido: ${z.prettifyError(parsed.error)}`);
  }
  return Object.freeze(merged) as T;
}
